import React from "react";
import { Outlet, Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import useAsyncRequest from "hooks/useAsyncRequest";
import { showTitleWhenClipped } from "utils/showTitleWhenClipped";
import { LoadablePage } from "shared/LoadablePage";
import CopyToClipboardWrapper from "components/CopyToClipboardWrapper";
import { EntityProvider } from "components/EntityProvider/EntityProvider";
import NavigationSidebarLayout, { Navigation } from "components/NavigationSidebarLayout/NavigationSidebarLayout";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { orderRoutes } from "areas/orders/order-routes";
import { billingAccountRoutes, returnToQuoteParam, withReturnToQuote } from "../billing-account-routes";
import { getBillingAccountByCode } from "../services/billingAccountService";

function getValueOrFallbackFor(billingAccountCode?: string) {
  return billingAccountCode || "UNKNOWN_BILLING_ACCOUNT";
}

export function BillingAccountDetailLayout() {
  const { billingAccountCode } = useParams<{ billingAccountCode: string }>();
  const [searchParams] = useSearchParams();
  const returnToQuoteOdNumber = searchParams.get(returnToQuoteParam);

  const {
    data: billingAccount,
    loading,
    error,
    refresh,
  } = useAsyncRequest(getBillingAccountByCode, getValueOrFallbackFor(billingAccountCode));

  const navLinks = [
    Navigation.navItem(
      "Details",
      withReturnToQuote(billingAccountRoutes.detail(billingAccountCode ?? "").root, returnToQuoteOdNumber),
      true,
      RbacPolicies.BillingAccounts.Read
    ),
    Navigation.navItem(
      "Subscriptions",
      withReturnToQuote(billingAccountRoutes.detail(billingAccountCode ?? "").subscriptions, returnToQuoteOdNumber),
      false,
      RbacPolicies.BillingAccounts.Read
    ),
    Navigation.navItem(
      "History",
      withReturnToQuote(billingAccountRoutes.detail(billingAccountCode ?? "").history, returnToQuoteOdNumber),
      false,
      RbacPolicies.BillingAccounts.Read
    ),
  ];

  const legalName = billingAccount?.legalName ?? "";

  const preNavbarComponent = (
    <Stack spacing={0.5}>
      <Typography variant="body1" noWrap onMouseEnter={showTitleWhenClipped(legalName)}>
        {legalName}
      </Typography>
      <CopyToClipboardWrapper value={billingAccountCode}>
        <Tooltip title="Billing account code">
          <span>{billingAccountCode}</span>
        </Tooltip>
      </CopyToClipboardWrapper>
    </Stack>
  );

  return (
    <LoadablePage loading={loading && !billingAccount} error={error}>
      <EntityProvider entity={billingAccount} refresh={refresh}>
        {returnToQuoteOdNumber && (
          <Box sx={{ mb: 2 }}>
            <Button
              component={RouterLink}
              to={orderRoutes.detail(returnToQuoteOdNumber).root}
              variant="outlined"
              startIcon={<ArrowBackIcon />}
            >
              Return to order
            </Button>
          </Box>
        )}
        <NavigationSidebarLayout navLinks={navLinks} name={"Billing account"} preNavbarComponent={preNavbarComponent}>
          <Outlet />
        </NavigationSidebarLayout>
      </EntityProvider>
    </LoadablePage>
  );
}
